import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export type UserRole = 'user' | 'instructor' | 'principal' | 'coordinator' | 'admin' | 'mentor';

interface RoleInfo {
  role: UserRole | null;
  permissions: string[];
  isAdmin: boolean;
  isCoordinator: boolean;
  isPrincipal: boolean;
  isInstructor: boolean;
  isMentor: boolean;
  isUser: boolean;
  loading: boolean;
}

export function useRole(): RoleInfo {
  const { user } = useAuth();
  const [role, setRole] = useState<UserRole | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUserRole = async () => {
      if (!user?.id) {
        setRole(null);
        setPermissions([]);
        setLoading(false);
        return;
      }

      try {
        // Get user's highest role
        const { data: roleData, error: roleError } = await supabase
          .rpc('get_user_role', { p_user_id: user.id });

        if (roleError) throw roleError;

        const userRole = roleData as UserRole;
        setRole(userRole);

        // Get permissions for this role (including inherited permissions)
        if (userRole) {
          const { data: permissionsData, error: permissionsError } = await supabase
            .from('role_permissions')
            .select('permission')
            .in('role', getRoleHierarchy(userRole));

          if (permissionsError) throw permissionsError;

          setPermissions(permissionsData?.map(p => p.permission) || []);
        } else {
          setPermissions([]);
        }
      } catch (error) {
        console.error('Error fetching user role:', error);
        setRole(null);
        setPermissions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchUserRole();
  }, [user?.id]);

  // Helper function to get role hierarchy for permission inheritance
  const getRoleHierarchy = (userRole: UserRole): UserRole[] => {
    switch (userRole) {
      case 'admin':
        return ['admin', 'coordinator', 'principal', 'instructor', 'mentor', 'user'];
      case 'coordinator':
        return ['coordinator', 'principal', 'instructor', 'mentor', 'user'];
      case 'principal':
        return ['principal', 'instructor', 'mentor', 'user'];
      case 'instructor':
        return ['instructor', 'user'];
      case 'mentor':
        return ['mentor', 'user'];
      case 'user':
        return ['user'];
      default:
        return [];
    }
  };

  const isAdmin = role === 'admin';
  const isCoordinator = role === 'coordinator' || isAdmin;
  const isPrincipal = role === 'principal' || isCoordinator;
  const isInstructor = role === 'instructor' || isPrincipal;
  const isMentor = role === 'mentor' || isInstructor;
  const isUser = role === 'user' || isInstructor || isMentor;

  return {
    role,
    permissions,
    isAdmin,
    isCoordinator,
    isPrincipal,
    isInstructor,
    isMentor,
    isUser,
    loading
  };
}

// Helper function to check if user has specific permission
export const usePermission = (permission: string): boolean => {
  const { permissions } = useRole();
  return permissions.includes(permission);
};

// Helper function to check role programmatically
export const checkUserRole = async (userId: string, requiredRole: UserRole): Promise<boolean> => {
  try {
    const { data, error } = await supabase
      .rpc('user_has_role_or_higher', { 
        p_user_id: userId, 
        p_required_role: requiredRole 
      });

    if (error) throw error;
    return data || false;
  } catch (error) {
    console.error('Error checking user role:', error);
    return false;
  }
};

// Helper function to check permission programmatically
export const checkUserPermission = async (userId: string, permission: string): Promise<boolean> => {
  try {
    const { data, error } = await supabase
      .rpc('user_has_permission', { 
        p_user_id: userId, 
        p_permission: permission 
      });

    if (error) throw error;
    return data || false;
  } catch (error) {
    console.error('Error checking user permission:', error);
    return false;
  }
};